import Navigation from '@/components/Navigation';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ERPDetailsModal } from '@/components/ERPDetailsModal';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Database, 
  Zap, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Clock,
  Users,
  FileText,
  TrendingUp,
  Award,
  Globe,
  Link2,
  Server
} from 'lucide-react';
import oracleLogo from '@/assets/logos/oracle-logo.png';
import yardiLogo from '@/assets/logos/yardi-logo.png';
import netSuiteLogo from '@/assets/logos/netsuite-logo.png';
import sapLogo from '@/assets/logos/sap-logo.png';
import dynamicsLogo from '@/assets/logos/microsoft-dynamics-logo.png';

const Integrations = () => {
  // Core ERP Partners - focus on the big 4
  const coreERPs = [
    {
      name: "Oracle EBS",
      slug: "oracle",
      logo: oracleLogo,
      description: "Native API integration with Oracle E-Business Suite for real-time AP automation",
      capabilities: ["Real-time invoice validation", "Duplicate detection via API", "Automated GL coding", "Purchase order matching"],
      methodology: "Proven Implementation",
      badge: "Most Popular",
      badgeColor: "bg-accent text-accent-foreground"
    },
    {
      name: "Oracle Cloud ERP",
      slug: "oracle",
      logo: oracleLogo,
      description: "Cloud-native integration with Oracle Fusion for modern AP workflows",
      capabilities: ["REST API integration", "Real-time data sync", "Advanced workflow routing", "Cloud security compliance"],
      methodology: "Zero Downtime Deployment",
      badge: "Fastest Setup",
      badgeColor: "bg-primary text-primary-foreground"
    },
    {
      name: "Yardi Voyager",
      slug: "yardi",
      logo: yardiLogo,
      description: "Property management-optimized integration for real estate and housing authorities",
      capabilities: ["Property-specific GL coding", "Vendor management sync", "Multi-property processing", "Maintenance invoice routing"],
      methodology: "Streamlined Process",
      badge: "Industry Leader",
      badgeColor: "bg-secondary text-secondary-foreground"
    },
    {
      name: "SAP",
      slug: "sap",
      logo: sapLogo,
      description: "Deep integration with SAP ECC and S/4HANA for enterprise-scale AP automation",
      capabilities: ["MIRO replacement workflow", "Real-time posting to SAP", "Multi-company code support", "Tolerance-based matching"],
      methodology: "Enterprise Deployment",
      badge: "Enterprise",
      badgeColor: "bg-primary text-primary-foreground"
    },
    {
      name: "NetSuite",
      slug: "netsuite",
      logo: netSuiteLogo,
      description: "Native integration with Oracle NetSuite OneWorld for cloud-first AP automation",
      capabilities: ["Subsidiary-aware processing", "SuiteFlow approval routing", "Vendor bill automation", "Project-linked invoicing"],
      methodology: "Cloud-Native Setup",
      badge: "Cloud Native",
      badgeColor: "bg-secondary text-secondary-foreground"
    },
    {
      name: "Microsoft Dynamics",
      slug: "dynamics",
      logo: dynamicsLogo,
      description: "Integration with Dynamics 365 F&O and Business Central for AP transformation",
      capabilities: ["Legal entity routing", "Intercompany processing", "Vendor invoice journaling", "D365 workflow integration"],
      methodology: "Phased Rollout",
      badge: "Multi-Entity",
      badgeColor: "bg-accent text-accent-foreground"
    }
  ];

  const technicalCapabilities = [
    {
      icon: <Database className="w-6 h-6 text-primary" />,
      title: "Bi-Directional Data Sync",
      description: "Real-time synchronization ensures your ERP and EZ Cloud are always in perfect alignment"
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Enterprise Security",
      description: "Bank-grade encryption, VPN connections, and SOC 2 compliance for all integrations"
    },
    {
      icon: <Zap className="w-6 h-6 text-primary" />,
      title: "Zero Downtime Setup",
      description: "Parallel processing during implementation means no disruption to your current workflows"
    },
    {
      icon: <Server className="w-6 h-6 text-primary" />,
      title: "API-First Architecture",
      description: "Native API connections provide faster, more reliable integration than file-based approaches"
    }
  ];

  const caseStudies = [
    {
      company: "Housing Authority of Covington",
      erp: "Yardi Voyager",
      challenge: "Processing invoices across multiple properties with insurance certificate management and complex approval workflows",
      result: "71% processing time reduction (7 days to 2 days), 80% error reduction",
      savings: "$79K annually",
      testimonial: "EZ Cloud has made my life in a busy accounts payable environment more efficient. The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together but they have, and it has been seamless."
    },
    {
      company: "Mid-Market Manufacturing",
      erp: "Oracle EBS", 
      challenge: "Complex 3-way matching across multiple locations with high-volume invoice processing",
      result: "Streamlined approval workflows, improved accuracy with automated GL coding",
      savings: "Significant efficiency gains",
      testimonial: "The Oracle integration handles our complex workflows seamlessly - EZ Cloud feels like a native part of our ERP system."
    }
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="ERP Integrations — Native Oracle, Yardi, NetSuite, SAP, Dynamics | EZ Cloud"
        description="Prebuilt, EZ Cloud-maintained integrations for Oracle Fusion + EBS, NetSuite, Yardi Voyager, SAP, and Dynamics 365. Invoice metadata, payments, and chart-of-accounts flow bi-directionally."
        canonical="https://ezcloud.ai/integrations"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero Section - ERP Integration Focus */}
      <section className="pt-20 pb-20 bg-hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Connect EZ Cloud to your ERP —
              <span className="block text-accent">Oracle, NetSuite, Yardi, SAP, Dynamics</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-10 text-white/90 max-w-3xl mx-auto">
              Each one has a prebuilt, EZ Cloud-maintained integration.
              Invoices, intercompany records, payment data, and linked images flow into your ERP;
              expense accounts, POs, vendors, and chart-of-accounts flow back.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Database className="w-5 h-5 mr-2" />
                See Your ERP Integration
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="text-lg px-8 py-4"
                asChild
              >
                <Link to="/integrations/oracle">
                  Explore the Oracle integration
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Core ERP Partners Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Our Core ERP Partners
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Deep, native integrations built specifically for these leading ERP systems. 
              Most of our customers use one of these four platforms.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {coreERPs.map((erp, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring hover:-translate-y-1 bg-white relative overflow-hidden">
                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <Badge className={`${erp.badgeColor} text-sm px-3 py-1`}>
                    {erp.badge}
                  </Badge>
                </div>

                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <div>
                      <img
                        src={erp.logo}
                        alt={`${erp.name} ERP logo`}
                        className="h-12 w-auto"
                      />
                    </div>
                    <div>
                      <CardTitle className="text-2xl font-bold text-foreground">{erp.name}</CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {erp.methodology}
                      </div>
                    </div>
                  </div>
                  <p className="text-muted-foreground">{erp.description}</p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-3 mb-6">
                    {erp.capabilities.map((capability, capIndex) => (
                      <div key={capIndex} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                        {capability}
                      </div>
                    ))}
                  </div>

                  {erp.slug ? (
                    <div className="space-y-2">
                      <Button asChild variant="outline" className="w-full group hover:bg-accent hover:text-accent-foreground">
                        <Link to={`/integrations/${erp.slug}`}>
                          See full {erp.name} page
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                      <ERPDetailsModal erp={erp}>
                        <button className="text-sm text-muted-foreground hover:text-foreground underline">
                          Or open quick-view
                        </button>
                      </ERPDetailsModal>
                    </div>
                  ) : (
                    <ERPDetailsModal erp={erp}>
                      <Button variant="outline" className="w-full group hover:bg-accent hover:text-accent-foreground">
                        View {erp.name} Details
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </ERPDetailsModal>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Technical Integration Capabilities
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade integration architecture designed for reliability, security, and performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {technicalCapabilities.map((capability, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-medium hover:shadow-large transition-spring">
                <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg">
                  {capability.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{capability.title}</h3>
                  <p className="text-muted-foreground">{capability.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Integration Success Stories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real results from real customers using our ERP integrations.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {caseStudies.map((study, index) => (
              <Card key={index} className="border-none shadow-large bg-white p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{study.company}</h3>
                    <Badge variant="secondary" className="mt-2">{study.erp}</Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-accent">{study.savings}</div>
                    <div className="text-sm text-muted-foreground">Annual Savings</div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Challenge</h4>
                    <p className="text-muted-foreground text-sm">{study.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Result</h4>
                    <p className="text-muted-foreground text-sm">{study.result}</p>
                  </div>
                </div>

                <div className="border-l-4 border-accent pl-4">
                  <p className="text-muted-foreground italic mb-2">"{study.testimonial}"</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-hero-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Integrate with Your ERP?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Get a custom integration assessment for your ERP system. Our team will show you 
              exactly how EZ Cloud connects with your existing infrastructure.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Clock className="w-5 h-5 mr-2" />
                Schedule Integration Demo
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="text-lg px-8 py-4"
                onClick={() => openCalendlyDemo()}
              >
                Contact Integration Team
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <p className="text-sm text-white/70">
              🚀 Enterprise-grade implementations • 💡 Zero downtime setup • 🔒 Enterprise security included
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Integrations;